import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Scale } from "lucide-react";
import Button from "./Button";
import { useCompare } from "../context/CompareContext";
import logo from "../assets/images/logo-fix.png";

const NAV = [
  { to: "/schools", label: "Jelajahi Sekolah" },
  { to: "/match", label: "AI School Match" },
  { to: "/compare", label: "Bandingkan" },
  { to: "/tentang", label: "Tentang" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count } = useCompare();


  // Handle route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    // Check initial position
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Determine top-level classes
  const isTransparent = !scrolled && !open;
  
  const headerClass = `sticky top-0 z-50 transition-all duration-300 print:hidden ${
    isTransparent 
      ? "bg-transparent border-transparent"
      : "bg-warm/95 backdrop-blur-md border-b border-ink/10 shadow-[0_4px_20px_-10px_rgba(33,26,77,0.1)]"
  }`;

  return (
    <header className={headerClass}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-18 py-2">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img src={logo} alt="MySchoolMate Logo" className="h-8 sm:h-10 w-auto" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-2">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-pill font-bold text-sm transition-colors ${
                    isActive 
                      ? "bg-ink text-warm" 
                      : isTransparent
                        ? "text-ink hover:bg-ink/10 hover:text-ink"
                        : "text-ink/80 hover:bg-cream-2 hover:text-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/compare"
              className={`relative p-2 rounded-full transition-colors ${
                isTransparent ? "hover:bg-ink/10" : "hover:bg-cream-2"
              }`}
              aria-label="Bandingkan"
              data-testid="nav-compare-icon"
            >
              <Scale size={20} className="text-ink" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-coral border-2 border-warm text-[11px] font-bold flex items-center justify-center text-ink">
                  {count}
                </span>
              )}
            </Link>
            <Button
              href="https://app.emergent.sh/showcase/building-indonesia/d761c7db-67e8-4d11-9ff2-e0580b551416"
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              variant="secondary"
              data-testid="nav-support"
            >
              Beri Dukungan
            </Button>
            <Button to="/signup" size="sm" variant="yellow" data-testid="nav-cta">
              Bergabung
            </Button>
          </div>

          {/* Mobile toggle */}
          <button
            className={`lg:hidden p-2 rounded-lg border-2 ${
              isTransparent ? "border-transparent hover:bg-ink/10" : "border-ink bg-warm"
            }`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Buka menu"
            data-testid="nav-hamburger"
          >
            {open ? <X size={22} className="text-ink" /> : <Menu size={22} className="text-ink" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden border-t border-ink/10 bg-warm shadow-soft absolute w-full" data-testid="mobile-menu">
          <nav className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-2xl font-semibold ${
                    isActive ? "bg-ink text-warm" : "text-ink hover:bg-cream-2"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex gap-3 pt-3">

              <Button
                href="https://app.emergent.sh/showcase/building-indonesia/d761c7db-67e8-4d11-9ff2-e0580b551416"
                target="_blank"
                rel="noopener noreferrer"
                variant="secondary"
                className="flex-1"
                data-testid="mobile-support"
              >
                Beri Dukungan
              </Button>
              <Button to="/signup" variant="yellow" className="flex-1" data-testid="mobile-cta">
                Bergabung
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
